<template>
  <div class="accordion" :class="props.status" :style="variantStyle">
    <button
      class="accordion-header"
      @click="onClick"
      @mouseover="onHover"
      @mouseleave="onHoverLeave"
      @focus="onHover"
      @blur="onHoverLeave"
      :aria-expanded="!!isOpen"
      :aria-controls="'content-' + props.id"
      :aria-label="`${isOpen ? 'Collapse' : 'Expand'}`"
      type="button"
      data-test="accordion-header"
    >
      <slot name="trigger"></slot>
      <span>
        <img
          src="../../assets/button-icons/chevron-down-grey.svg"
          :alt="`chevron ${isOpen ? 'up' : 'down'}`"
          class="chevron-icon"
          :class="props.status"
        />
      </span>
    </button>
    <Transition name="accordion">
      <div
        v-show="isOpen"
        class="accordion-content"
        :id="'content-' + props.id"
        data-test="accordion-content"
      >
        <slot></slot>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Status } from "../../typings/shared/status";
import { Variant } from "../../typings/shared/levels-variant";

const props = defineProps<{ id: string; status: Status; isOpen: boolean; variant: Variant }>();

const emit = defineEmits(["accordionOpened", "accordionClosed"]);

const onClick = () => {
  emit(props.isOpen ? "accordionClosed" : "accordionOpened", props.id);
};

const variantStyle = computed(() => {
  if (props.variant === "team") {
    return {
      "--bg-active": "#b0bb36",
      "--active-border": "solid 1px #b0bb36",
    };
  }
  if (props.variant === "user") {
    return {
      "--bg-active": "#FCAF17",
      "--active-border": "solid 1px #FCAF17",
    };
  }
  return {};
});

const REGULAR_WIDTH = "98%";
const HOVER_WIDTH = "100%";

const accordionWidth = ref(REGULAR_WIDTH);

function onHover() {
  if (!props.isOpen) {
    accordionWidth.value = HOVER_WIDTH;
  }
}

function onHoverLeave() {
  if (!props.isOpen) {
    accordionWidth.value = REGULAR_WIDTH;
  }
}
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

@default-border: solid 1px @btn-default-border;
@uncompleted-border: solid 1px @off-white;
@active-border: var(--active-border);

@bg-uncompleted: @off-white;
@bg-active: var(--bg-active);
@bg-completed: #ffffff;

@text-colour-default: @form-text-color;
@text-colour-active: #ffffff;

.accordion {
  width: 98%;
  transition: width 0.3s ease;

  @media screen and (min-width: @screen-xs-min) {
    width: v-bind(accordionWidth);
  }
}

.accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: @bg-uncompleted;
  padding: 10px 20px;
  font-weight: bold;
  color: @form-text-color;
  cursor: pointer;
  border-radius: 10px 10px 0 0;
  width: 100%;
  text-align: left;

  img {
    transition: transform 0.5s ease;
  }

  .chevron-icon {
    opacity: 0;
  }

  &:hover,
  &:focus {
    .chevron-icon {
      opacity: 1;
    }
  }
}

.accordion.active {
  .accordion-header {
    background-color: @bg-active;
    border: @active-border;
  }

  .chevron-icon {
    opacity: 1;
  }
}

.accordion.completed {
  .accordion-header {
    background-color: @bg-completed;
    border: @active-border;

    &:hover {
      outline: solid 1px @bg-completed;
    }
  }
}

.accordion.uncompleted {
  .accordion-header {
    background-color: @bg-uncompleted;
    border: @uncompleted-border;
  }
}

.accordion-header[aria-expanded="true"] {
  img {
    transform: rotate(180deg);
  }
}

.uncompleted & .accordion-header[aria-expanded="true"] {
  border: @default-border;
}

.active & .accordion-header[aria-expanded="true"],
.completed & .accordion-header[aria-expanded="true"] {
  border: @active-border;
}

.chevron-icon.active {
  filter: brightness(0) invert(1);
}

.accordion-content {
  overflow: hidden;
  border-bottom: @default-border;
  border-left: @default-border;
  border-right: @default-border;
  border-radius: 0 0 10px 10px;
}

.active,
.completed {
  .accordion-content {
    border-bottom: @active-border;
    border-left: @active-border;
    border-right: @active-border;
  }
}

/* Transition styles */
.accordion-enter-active,
.accordion-leave-active {
  transition: max-height 0.5s ease;
}

.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
}

.accordion-leave-from,
.accordion-enter-to {
  max-height: 1000px;
}
</style>
